import { useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { DataTable, ErrorState, Spinner, StatusBadge } from '../../components/ui.jsx';
import { BarList, StatTile } from '../../components/charts.jsx';
import { useFetch, useTitle } from '../../lib/hooks.js';
import { formatDate, formatXaf, rentPeriodNoun } from '../../lib/format.js';

const monthLabel = (value) => {
  const date = new Date(`${value}-01T12:00:00Z`);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString('en-GB', { month: 'short', year: '2-digit', timeZone: 'UTC' });
};

const RentRoll = () => {
  useTitle('Rent');
  const [params, setParams] = useSearchParams();
  const filter = params.get('filter') || 'all';
  const [showReceipts, setShowReceipts] = useState(false);

  const roll = useFetch(`/landlord/rent-roll?filter=${filter}`, [filter]);
  const receipts = useFetch(showReceipts ? '/landlord/receipts' : null, [showReceipts]);

  if (roll.loading && !roll.data) return <Spinner />;
  if (roll.error) {
    return (
      <div className="container section">
        <ErrorState message={roll.error} onRetry={roll.reload} />
      </div>
    );
  }

  const { items, summary, collectedByMonth, momoMode } = roll.data;

  const columns = [
    {
      key: 'what',
      header: 'Property and tenant',
      render: (row) => (
        <div className="cell-stack">
          <Link to={`/landlord/rent/${row.id}`} className="cell-title">
            {row.unitLabel ? `${row.unitLabel}, ` : ''}
            {row.propertyTitle}
          </Link>
          <span className="muted">{row.tenantName}</span>
          <a className="muted" href={`tel:${row.tenantPhone}`}>
            {row.tenantPhone}
          </a>
        </div>
      )
    },
    {
      key: 'rent',
      header: 'Rent',
      render: (row) => (
        <div className="cell-stack">
          <strong>{formatXaf(row.rent)}</strong>
          <span className="muted">
            a {rentPeriodNoun(row.billingPeriod)}, day {row.rentDueDay} of the month
          </span>
        </div>
      )
    },
    {
      key: 'next',
      header: 'Next due',
      render: (row) =>
        row.nextDue ? (
          <div className="cell-stack">
            <span>{row.nextDue.label}</span>
            <span className="muted">
              {formatXaf(row.nextDue.outstanding)} by {formatDate(row.nextDue.dueDate)}
            </span>
          </div>
        ) : (
          <span className="muted">Nothing outstanding</span>
        )
    },
    {
      key: 'balance',
      header: 'Balance',
      render: (row) => (
        <div className="cell-stack">
          {(row.dueNow ?? row.balance) > 0 ? (
            <strong className="owing">{formatXaf(row.dueNow ?? row.balance)} due now</strong>
          ) : row.balance < 0 ? (
            <span>{formatXaf(-row.balance)} in credit</span>
          ) : (
            <span className="muted">Up to date</span>
          )}
          {row.overdue > 0 ? <span className="overdue-flag">{formatXaf(row.overdue)} overdue</span> : null}
          {row.balance > (row.dueNow ?? 0) ? (
            <span className="muted">{formatXaf(row.balance - (row.dueNow ?? 0))} ahead for the year</span>
          ) : null}
        </div>
      )
    },
    {
      key: 'status',
      header: 'Lease',
      render: (row) => (
        <StatusBadge
          status={row.status === 'active' ? 'approved' : 'withdrawn'}
          label={row.status === 'active' ? 'In force' : row.status === 'ended' ? 'Ended' : 'Terminated'}
        />
      )
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (row) => (
        <div className="cell-actions">
          <Link to={`/landlord/rent/${row.id}`} className="btn btn-primary btn-sm">
            Collect
          </Link>
          <a className="btn btn-ghost btn-sm" href={`tel:${row.tenantPhone}`}>
            Call
          </a>
        </div>
      )
    }
  ];

  const receiptColumns = [
    {
      key: 'number',
      header: 'Receipt',
      render: (receipt) => (
        <div className="cell-stack">
          <Link to={`/receipts/${receipt.id}`}>{receipt.receiptNumber}</Link>
          {receipt.voided ? <span className="void-flag">cancelled</span> : null}
        </div>
      )
    },
    { key: 'paidAt', header: 'Date', render: (receipt) => formatDate(receipt.paidAt) },
    {
      key: 'who',
      header: 'From',
      render: (receipt) => (
        <div className="cell-stack">
          <span>{receipt.tenantName}</span>
          <span className="muted">
            {receipt.unitLabel ? `${receipt.unitLabel}, ` : ''}
            {receipt.propertyTitle}
          </span>
        </div>
      )
    },
    { key: 'amount', header: 'Amount', render: (receipt) => formatXaf(receipt.amount) },
    {
      key: 'actions',
      header: '',
      render: (receipt) => (
        <Link to={`/receipts/${receipt.id}`} className="btn btn-ghost btn-sm">
          View or print
        </Link>
      )
    }
  ];

  return (
    <div className="container section">
      <header className="page-head">
        <div>
          <h1>Rent</h1>
          <p className="muted">What is due, what has come in, and who is behind.</p>
        </div>
        <div className="row-actions">
          <button type="button" className="btn btn-outline" onClick={() => setShowReceipts((value) => !value)}>
            {showReceipts ? 'Hide the receipt book' : 'Receipt book'}
          </button>
          <Link to="/landlord/utilities" className="btn btn-ghost">
            Utilities
          </Link>
          <Link to="/landlord/leases" className="btn btn-ghost">
            Leases
          </Link>
        </div>
      </header>

      {momoMode === 'mock' ? (
        <div className="notice notice-info">
          <strong>Mobile money is in demonstration mode.</strong> Tenants can go through the whole "pay with MTN or
          Orange" flow and receipts are issued, but no real money moves. Set the provider credentials to switch it on.
        </div>
      ) : null}

      <div className="stat-row">
        <StatTile value={summary.activeLeases} label="Leases in force" />
        <StatTile value={formatXaf(summary.expectedMonthly)} label="Expected each month" />
        <StatTile value={formatXaf(summary.collectedThisMonth)} label="Collected this month" />
        <StatTile
          value={formatXaf(summary.dueNow ?? summary.outstanding)}
          label="Due now"
          note={
            (summary.overdue === 0
              ? 'Nothing overdue'
              : `${formatXaf(summary.overdue)} of it overdue`) +
            ((summary.dueNow ?? summary.outstanding) < summary.outstanding
              ? ` - ${formatXaf(summary.outstanding - (summary.dueNow ?? 0))} more billed for the lease year`
              : '')
          }
          tone={summary.overdue > 0 ? 'warn' : undefined}
        />
      </div>

      {summary.tenantsBehind > 0 ? (
        <div className="notice notice-warn">
          <strong>
            {summary.tenantsBehind} tenant{summary.tenantsBehind === 1 ? ' is' : 's are'} behind on rent.
          </strong>{' '}
          Use the overdue tab to see who, and the call button to chase them.
        </div>
      ) : null}

      {collectedByMonth.length > 1 ? (
        <div className="chart-grid">
          <BarList
            title="Rent collected"
            subtitle="Last twelve months"
            keyHeader="Month"
            rows={collectedByMonth.map((row) => ({
              key: row.month,
              label: monthLabel(row.month),
              value: row.total
            }))}
            valueFormatter={formatXaf}
            accessibleLabel="Rent collected each month over the last year"
          />
        </div>
      ) : null}

      <div className="tab-row">
        {[
          ['all', 'All leases'],
          ['owing', 'Owing something'],
          ['overdue', 'Overdue'],
          ['clear', 'Up to date']
        ].map(([value, label]) => (
          <button
            key={value}
            type="button"
            className={`tab ${filter === value ? 'is-current' : ''}`}
            onClick={() => setParams(value === 'all' ? {} : { filter: value })}
          >
            {label}
          </button>
        ))}
      </div>

      <DataTable
        columns={columns}
        rows={items}
        empty={{
          title: filter === 'all' ? 'No leases in force' : 'Nothing in this group',
          message:
            filter === 'all'
              ? 'Once a lease is signed by both parties its rent schedule appears here.'
              : 'Try another tab.'
        }}
      />

      {showReceipts ? (
        <section className="section-block">
          <div className="section-head">
            <h2>Receipt book</h2>
            <span className="muted">Every receipt you have issued, newest first.</span>
          </div>
          {receipts.loading ? <Spinner /> : null}
          {receipts.error ? <ErrorState message={receipts.error} onRetry={receipts.reload} /> : null}
          {receipts.data ? (
            <DataTable
              columns={receiptColumns}
              rows={receipts.data.items}
              empty={{ title: 'No receipts yet', message: 'Record a payment and the receipt appears here.' }}
            />
          ) : null}
        </section>
      ) : null}
    </div>
  );
};

export default RentRoll;
